import { z } from "zod";
import { paginationMetaDtoSchema } from "@nasam/kernel";

const required = z.string().trim().min(1);

export const operationalUserStatusDtoSchema = z.enum([
  "PENDING_ACTIVATION",
  "ACTIVE",
  "INACTIVE",
]);

const namedRefDtoSchema = z.object({
  id: required,
  nameEn: z.string(),
  nameAr: z.string(),
});

// The one row shape every /users endpoint returns (list, archived, detail).
export const operationalUserDtoSchema = z.object({
  id: required,
  username: z.string(),
  email: z.string(),
  nameEn: z.string(),
  nameAr: z.string(),
  phoneNumber: z.string().nullable().optional(),
  status: operationalUserStatusDtoSchema,
  accountId: z.string().nullable().optional(),
  branchId: z.string().nullable().optional(),
  departmentId: z.string().nullable().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
  branch: namedRefDtoSchema,
  // Null (or absent) for a user with no department (departmentId null).
  department: namedRefDtoSchema.nullable().optional(),
});

export const operationalUserListResponseDtoSchema = z.object({
  data: z.array(operationalUserDtoSchema),
  meta: paginationMetaDtoSchema,
});

// Only the detail (GET /users/{id}) response carries the resolved
// permissions -- no role reference, just the grants themselves.
export const operationalUserDetailDtoSchema = operationalUserDtoSchema.extend({
  permissions: z.array(z.string()).optional(),
});

export const operationalUserResponseDtoSchema = z.object({
  message: z.string().optional(),
  data: operationalUserDetailDtoSchema,
});

export type OperationalUserDto = z.infer<typeof operationalUserDtoSchema>;
export type OperationalUserDetailDto = z.infer<
  typeof operationalUserDetailDtoSchema
>;

export const operationalUserMessageResponseDtoSchema = z.object({
  message: z.string().optional(),
});

// The archived list's row: the same shape, plus when it was archived.
export const archivedOperationalUserDtoSchema = operationalUserDtoSchema.extend({
  archivedAt: required,
});

export const archivedOperationalUserListResponseDtoSchema = z.object({
  data: z.array(archivedOperationalUserDtoSchema),
  meta: paginationMetaDtoSchema,
});

export type ArchivedOperationalUserDto = z.infer<
  typeof archivedOperationalUserDtoSchema
>;

// templateId is deliberately left out: the final `permissions` set always
// replaces the template's, so there is nothing for the backend to copy.
export const operationalUserCreateDtoSchema = z.object({
  nameEn: required,
  nameAr: required,
  username: required,
  email: required,
  phoneNumber: required.optional(),
  branchId: required,
  departmentId: required,
  permissions: z.array(z.string()),
});

export type OperationalUserCreateDto = z.infer<
  typeof operationalUserCreateDtoSchema
>;

// Username and email are fixed after creation, so neither is here.
export const operationalUserUpdateDtoSchema = z.object({
  nameEn: required,
  nameAr: required,
  phoneNumber: required.optional(),
  status: z.enum(["ACTIVE", "INACTIVE"]).optional(),
  branchId: required,
  departmentId: required,
  permissions: z.array(z.string()),
});

export type OperationalUserUpdateDto = z.infer<
  typeof operationalUserUpdateDtoSchema
>;

// 409 on create when the username, email, phone number or either name
// belongs to an archived operational user -- offers a restore instead.
export const archivedOperationalUserMatchResponseDtoSchema = z.object({
  message: z.string().optional(),
  code: z.literal("ARCHIVED_USER_FOUND"),
  data: z.object({
    id: required,
    username: z.string().optional(),
    email: z.string().optional(),
    nameEn: z.string().optional(),
    nameAr: z.string().optional(),
    phoneNumber: z.string().nullable().optional(),
  }),
});

export type ArchivedOperationalUserMatchDto = z.infer<
  typeof archivedOperationalUserMatchResponseDtoSchema
>["data"];
